import React, { useMemo } from "react";
import type { SubcategoryAttributes } from "../../../types";
import type { CategoryAttributes, CategoryType } from "../../../types";
import { DataList, ListColumn, ListAction, Avatar, Icons } from "../../shared";

const typeLabel: Record<CategoryType, string> = {
  input: "Entrada",
  output: "Saída",
};

interface AllSubcategoriesListViewProps {
  subcategories: SubcategoryAttributes[];
  categories: CategoryAttributes[];
  onEdit: (subcategory: SubcategoryAttributes) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
  emptyMessage?: string;
}

export const AllSubcategoriesListView: React.FC<AllSubcategoriesListViewProps> = ({
  subcategories,
  categories,
  onEdit,
  onDelete,
  loading = false,
  emptyMessage = "Nenhuma categoria encontrada",
}) => {
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const columns: ListColumn<SubcategoryAttributes>[] = [
    {
      key: "name",
      label: "Categoria",
      width: "1.6fr",
      render: (item) => (
        <div className="flex items-center min-w-0">
          <Avatar text={item.name} size="md" variant="emerald" />
          <div className="ml-3 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-slate-200 truncate">{item.name}</p>
          </div>
        </div>
      ),
    },
    {
      key: "categoryId",
      label: "Categoria mãe",
      width: "1.4fr",
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-slate-400">
          {categoryById.get(item.categoryId)?.name ?? "—"}
        </span>
      ),
    },
    {
      key: "type",
      label: "Tipo",
      width: "0.9fr",
      render: (item) => {
        const cat = categoryById.get(item.categoryId);
        const t = cat?.type;
        if (!t) return <span className="text-sm text-gray-500 dark:text-slate-500">—</span>;
        return (
          <span
            className={`text-sm font-medium px-2 py-1 rounded-lg inline-block ${
              t === "input"
                ? "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300"
                : "bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300"
            }`}
          >
            {typeLabel[t]}
          </span>
        );
      },
    },
    {
      key: "plan",
      label: "Plano",
      width: "1fr",
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-slate-400">
          {categoryById.get(item.categoryId)?.plan ?? "—"}
        </span>
      ),
    },
  ];

  const actions: ListAction<SubcategoryAttributes>[] = [
    {
      label: "Editar",
      icon: <Icons.Edit />,
      onClick: (item) => onEdit(item),
      className:
        "p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200",
      title: "Editar categoria",
      disabled: () => loading,
    },
    {
      label: "Excluir",
      icon: <Icons.Delete />,
      onClick: (item) => onDelete(item.id),
      className:
        "p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200",
      title: "Excluir categoria",
      disabled: () => loading,
    },
  ];

  return (
    <DataList<SubcategoryAttributes>
      data={subcategories}
      columns={columns}
      actions={actions}
      keyExtractor={(item) => item.id}
      emptyMessage={emptyMessage}
      loading={loading}
      pageSize={10}
      itemLabel="categorias"
      onRowClick={(item) => onEdit(item)}
    />
  );
};
